// src/components/child/OnStreetEnforcementShimmer.jsx
// Skeleton shimmers for the On Street Enforcement overview tab.

import React from 'react';

const ShimmerBar = ({ w = 'w-full', h = 'h-3', className = '' }) => (
  <div className={`${w} ${h} rounded bg-muted-foreground/15 animate-pulse ${className}`} />
);

// Mirrors <KpiCard /> exactly: icon box + label + value.
export const KpiCardShimmer = () => (
  <div className="rounded-xl border border-border bg-card p-3.5 flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-muted-foreground/15 animate-pulse flex-shrink-0" />
    <div className="min-w-0 flex-1 space-y-1.5">
      <ShimmerBar w="w-14" h="h-2" className="opacity-70" />
      <ShimmerBar w="w-12" h="h-4" />
    </div>
  </div>
);

// Mirrors the CNS/Enforcement Overview collapsible card sections
// (grid-cols-6, matching the real `grid grid-cols-6 gap-3` layout).
export const CardsSectionShimmer = ({ count = 12 }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="flex items-center justify-between px-3.5 py-2.5">
      <div className="flex items-center gap-1.5" />
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 p-3.5 pt-0">
      {Array.from({ length: count }).map((_, i) => <KpiCardShimmer key={i} />)}
    </div>
  </div>
);

// Mirrors the single full-width "Revenue Summary" card: year/All tabs +
// Line/Stack toggle header, then a line-chart body (LineBar/StackedBarChart).
export const RevenueSummaryCardShimmer = () => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border gap-2">
      <div className="flex items-center gap-1 bg-secondary/60 rounded-md p-0.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <ShimmerBar key={i} w="w-9" h="h-5" className="rounded-md opacity-70" />
        ))}
      </div>
      <div className="flex items-center gap-1 bg-secondary/60 rounded-md p-0.5">
        <ShimmerBar w="w-11" h="h-5" className="rounded-md" />
        <ShimmerBar w="w-11" h="h-5" className="rounded-md" />
      </div>
    </div>
    <div className="p-4">
      <ShimmerBar w="w-64" h="h-3" className="mb-1.5 mx-auto" />
      <ShimmerBar w="w-48" h="h-2.5" className="mb-4 mx-auto opacity-70" />
      {/* Legend — mirrors LineBar's multi-series color key (CNS/CLAMP/TOW/Storage). */}
      <div className="flex items-center justify-center gap-4 mb-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-1.5 animate-pulse">
            <span className="w-2 h-2 rounded-full flex-shrink-0 bg-muted-foreground/25" />
            <ShimmerBar w="w-10" h="h-2" className="opacity-60" />
          </div>
        ))}
      </div>
      <div className="h-64 relative">
        <svg viewBox="0 0 900 220" className="w-full h-full" preserveAspectRatio="none">
          {[0, 44, 88, 132, 176, 220].map((y) => (
            <line key={y} x1="0" y1={y} x2="900" y2={y} stroke="currentColor" strokeWidth="1" className="text-muted-foreground/10" />
          ))}
          {[
            'M0,140 C90,110 180,70 270,90 C360,105 450,150 540,130 C630,110 720,60 810,45 L900,55',
            'M0,170 C90,160 180,150 270,155 C360,150 450,165 540,160 C630,150 720,140 810,135 L900,130',
            'M0,100 C90,95 180,120 270,105 C360,90 450,110 540,95 C630,80 720,100 810,85 L900,90',
            'M0,190 C90,185 180,190 270,180 C360,175 450,185 540,178 C630,170 720,175 810,168 L900,172',
          ].map((d, i) => (
            <path key={i} d={d} fill="none" stroke="currentColor" strokeWidth="2.5" className="text-muted-foreground/25 animate-pulse" />
          ))}
        </svg>
      </div>
      <div className="flex justify-between gap-1.5 mt-2 px-2">
        {Array.from({ length: 12 }).map((_, i) => <ShimmerBar key={i} w="w-6" h="h-2" className="opacity-50" />)}
      </div>
    </div>

    {/* Data table below the chart — mirrors LineBar's per-series rows (one per legend color). */}
    <div className="border-t border-border overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b border-border/50">
            <th className="px-1.5 py-1.5 w-28"></th>
            {Array.from({ length: 12 }).map((_, i) => (
              <th key={i} className="px-0.5 py-1.5"><ShimmerBar w="w-full" h="h-2" className="opacity-40 mx-auto" /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 4 }).map((_, ri) => (
            <tr key={ri} className="border-b border-border/30 last:border-b-0">
              <td className="px-1.5 py-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/20 flex-shrink-0" />
                  <ShimmerBar w="w-16" h="h-2" className="opacity-60" />
                </div>
              </td>
              {Array.from({ length: 12 }).map((_, ci) => (
                <td key={ci} className="px-0.5 py-1.5"><ShimmerBar w="w-8" h="h-2" className="opacity-45 mx-auto" /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-center py-1.5">
        <ShimmerBar w="w-14" h="h-2" className="opacity-40 mx-auto" />
      </div>
    </div>
  </div>
);

// Mirrors the full-width NewChartKPI card (CNS/Enforcement tabs + mode
// select + bar chart + axis labels).
export const MainChartShimmer = () => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border gap-2">
      <div />
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 bg-secondary/60 rounded-md p-0.5">
          <ShimmerBar w="w-10" h="h-5" className="rounded-md opacity-70" />
          <ShimmerBar w="w-16" h="h-5" className="rounded-md" />
        </div>
        <ShimmerBar w="w-24" h="h-7" className="rounded-md" />
      </div>
    </div>
    <div className="p-4">
      <ShimmerBar w="w-64" h="h-3" className="mb-1.5 mx-auto" />
      <ShimmerBar w="w-48" h="h-2.5" className="mb-5 mx-auto opacity-70" />
      <div className="relative h-56 px-2">
        <div className="absolute inset-0 flex items-end gap-1.5 px-2">
          {Array.from({ length: 30 }).map((_, i) => {
            const hp = 25 + ((i * 37) % 70);
            return <div key={i} className="flex-1 rounded-t bg-muted-foreground/15 animate-pulse" style={{ height: `${hp}%` }} />;
          })}
        </div>
        {/* Line overlays — mirror NewChartKPI's dual line series (transaction/occupancy) drawn over the bars. */}
        <svg viewBox="0 0 900 220" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <path
            d="M0,150 C90,120 180,90 270,110 C360,130 450,160 540,140 C630,120 720,80 810,60 L900,70"
            fill="none" stroke="currentColor" strokeWidth="2.5" className="text-muted-foreground/30 animate-pulse"
          />
          <path
            d="M0,80 C90,95 180,60 270,50 C360,42 450,70 540,55 C630,40 720,65 810,45 L900,50"
            fill="none" stroke="currentColor" strokeWidth="2.5" className="text-muted-foreground/20 animate-pulse"
          />
        </svg>
      </div>
      <div className="flex justify-between gap-1.5 mt-2 px-2">
        {Array.from({ length: 12 }).map((_, i) => <ShimmerBar key={i} w="w-6" h="h-2" className="opacity-50" />)}
      </div>
    </div>

    {/* Data table below the chart — mirrors NewChartKPI's revenue/transaction/occupancy rows. */}
    <div className="border-t border-border overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b border-border/50">
            <th className="px-1.5 py-1.5 w-28"></th>
            {Array.from({ length: 12 }).map((_, i) => (
              <th key={i} className="px-0.5 py-1.5"><ShimmerBar w="w-full" h="h-2" className="opacity-40 mx-auto" /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 3 }).map((_, ri) => (
            <tr key={ri} className="border-b border-border/30 last:border-b-0">
              <td className="px-1.5 py-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/20 flex-shrink-0" />
                  <ShimmerBar w="w-16" h="h-2" className="opacity-60" />
                </div>
              </td>
              {Array.from({ length: 12 }).map((_, ci) => (
                <td key={ci} className="px-0.5 py-1.5"><ShimmerBar w="w-8" h="h-2" className="opacity-45 mx-auto" /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-center py-1.5">
        <ShimmerBar w="w-14" h="h-2" className="opacity-40 mx-auto" />
      </div>
    </div>
  </div>
);

// Mirrors the "Enforcement KPI Summary" table. `columns` should match the
// real table's header columns so widths/counts line up.
export const KpiTableShimmer = ({ rows = 8, columns = [] }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="px-3.5 py-2.5 border-b border-border"><ShimmerBar w="w-40" h="h-3" /></div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-secondary/60 border-b border-border">
            <th className="text-left px-3.5 py-2.5"><ShimmerBar w="w-20" h="h-2" className="opacity-60" /></th>
            {columns.map(col => (
              <th key={col} className="text-right px-3 py-2.5"><ShimmerBar w="w-12" h="h-2" className="opacity-60 ml-auto" /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className={`border-b border-border/50 last:border-b-0 ${i % 2 === 1 ? 'bg-secondary/20' : ''}`}>
              <td className="px-3.5 py-2.5"><ShimmerBar w="w-24" h="h-2.5" /></td>
              {columns.map(col => (
                <td key={col} className="text-right px-3 py-2.5"><ShimmerBar w="w-10" h="h-2.5" className="ml-auto" /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Full-page skeleton, mirroring the real overview layout:
// CNS cards -> Enforcement cards -> Revenue Summary -> main chart -> KPI table.
export const OverviewShimmer = ({ tableColumns = [] }) => (
  <div className="space-y-4">
    <CardsSectionShimmer count={11} />
    <CardsSectionShimmer count={12} />
    <RevenueSummaryCardShimmer />
    <MainChartShimmer />
    <KpiTableShimmer rows={15} columns={tableColumns} />
  </div>
);
